import { get as getConfig } from '../../config/index'

// ===== STABLE PERSONALITY ID CONSTANTS =====

export const PERSONALITY_ARCHITECT_ID = 1
export const PERSONALITY_SAGE_ID = 2

// ===== SINGLE SOURCE OF TRUTH =====

// This array defines all personalities with their metadata
// Array order determines display priority (first = highest priority)
export const PERSONALITIES = [
  {
    id: PERSONALITY_ARCHITECT_ID,
    key: 'architect',
    displayName: 'Architect+',
    enabled: true,
  },
  {
    id: PERSONALITY_SAGE_ID,
    key: 'sage',
    displayName: 'Gaia Sage',
    enabled: true,
  },
  {
    id: 3, key: 'DLX-1', displayName: 'Larry', enabled: false,
  },
  {
    id: 4, key: 'DLX-2', displayName: 'Curly', enabled: false,
  },
  {
    id: 5, key: 'DLX-3', displayName: 'Moe', enabled: false,
  },

  // Future experimental personalities go here:
  // {
  //   id: 3,
  //   key: 'mystic',
  //   displayName: 'Mystic Oracle',
  //   enabled: false,  // Controlled by feature flag
  // },
] as const

// Default personality (Gaia Sage). NOTE: this is the *second* entry in PERSONALITIES,
// not the first — array order is display priority (Architect+ first), which is
// independent of the default selection.
export const DEFAULT_PERSONALITY_ID = PERSONALITY_SAGE_ID

// ===== FEATURE FLAG HELPERS =====

/**
 * Get the current experimental personalities feature flag value
 */
const isExperimentalEnabled = () => {
  const config = getConfig()
  return Boolean(config.features.ai.experimentalPersonalities)
}

/**
 * Get available personalities based on feature flag. Reads
 * config.features.ai.experimentalPersonalities to determine whether to include
 * experimental (enabled: false) personalities
 */
export const getAvailablePersonalities = () => {
  if (isExperimentalEnabled()) {
    return [...PERSONALITIES]
  }
  return PERSONALITIES.filter((p) => p.enabled)
}

// ===== LOOKUP FUNCTIONS =====

/**
 * Find a personality by ID
 */
export const getPersonalityById = (id: number | string) => {
  // For strings, require a strictly-numeric value so a URL param like '2abc'
  // can't loosely parseInt to 2 and falsely match a real personality.
  const numId = typeof id === 'string' ? Number(id) : id
  if (!Number.isInteger(numId)) return undefined
  return PERSONALITIES.find((p) => p.id === numId)
}

/**
 * Find a personality by key (case-insensitive)
 */
export const getPersonalityByKey = (key: string) => {
  return PERSONALITIES.find((p) => p.key.toLowerCase() === key.toLowerCase())
}

// ===== BACKWARD COMPATIBLE CONVERSION FUNCTIONS =====

/**
 * Get display name for a personality.
 *
 * This is the canonical, analytics-facing value for the `ai_persona` param
 * (AI-820 / CS3): both the PostHog and GA4 legs emit these display names.
 *
 * Resolution order for string inputs: try a key lookup first, then fall back to
 * an id lookup (the `oraclePersonalityId` URL param arrives as a numeric string
 * like '1'/'2' and must resolve to the same display name as the numeric id).
 * Unknown inputs and already-resolved display names pass through unchanged, so
 * applying this at the GA4 chokepoint is idempotent.
 */
export const getPersonalityDisplayName = (keyOrId: string | number) => {
  const personality = typeof keyOrId === 'string'
    ? getPersonalityByKey(keyOrId) || getPersonalityById(keyOrId)
    : getPersonalityById(keyOrId)
  return personality?.displayName || String(keyOrId)
}

/**
 * Convert personality ID to key
 */
export const getPersonalityKeyById = (id: string | number): string | undefined => {
  const personality = getPersonalityById(id)
  return personality?.key
}

/**
 * Convert personality key to ID (as string)
 */
export const getPersonalityIdByKey = (key: string): string | undefined => {
  const personality = getPersonalityByKey(key)
  return personality?.id.toString()
}

// ===== GRAPHQL HELPERS =====

/**
 * Get GraphQL where clause for filtering personalities
 * Automatically reads config.features.ai.experimentalPersonalities
 * to determine whether to include experimental personalities
 */
export const getEnabledPersonalitiesWhereClause = () => {
  return {
    id: { in: getAvailablePersonalities().map((p) => p.id) },
  }
}

// ===== SORTING =====

/**
 * Sort personalities by display priority (using array order from PERSONALITIES)
 * @param personalities - Array of personality objects with 'key' property
 */
export const sortPersonalitiesByDisplayPriority = <T extends { key: string }>(
  personalities: T[],
): T[] => {
  const keyToIndex = new Map<string, number>(
    PERSONALITIES.map((p, idx) => [p.key.toLowerCase(), idx]),
  )
  return [...personalities].sort((a, b) => {
    const indexA = keyToIndex.get(a.key.toLowerCase()) ?? 999
    const indexB = keyToIndex.get(b.key.toLowerCase()) ?? 999
    return indexA - indexB
  })
}
